import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { readStore, removeStore } from "../lib/storage";
import { type ClockState, EMPTY, type Times, tick, total } from "./clock";

const TICK_MS = 1000;
const SAVE_MS = 5000;

function add(a: Times, b: Times): Times {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) out[k] = (out[k] ?? 0) + v;
  return out;
}

/**
 * Time per section, kept in the database: one run for rehearsals, one per live session (`sessionCode`). Saved every
 * few seconds while running, on pause, and when the tab closes, so a reload or another device picks it up. Reset
 * finishes the run (it stays on record for the dashboard) and starts a fresh one.
 * Starts on the first move away from the start; T (or the timer itself) pauses and resumes.
 */
export function useClock(deckId: string, sectionId: string, sessionCode: string | null = null) {
  // State belongs to one run scope; a new session (or deck) starts from zero without a reset effect.
  const scope = `${deckId}:${sessionCode ?? ""}`;
  const [raw, setRaw] = useState<ClockState & { scope: string }>({ ...EMPTY, scope });
  const state: ClockState = raw.scope === scope ? raw : EMPTY;
  const setState = useCallback(
    (f: (s: ClockState) => ClockState) => setRaw((r) => ({ ...f(r.scope === scope ? r : EMPTY), scope })),
    [scope],
  );
  const section = useRef(sectionId);
  useLayoutEffect(() => {
    section.current = sectionId;
  }, [sectionId]);
  const last = useRef(0); // set when the clock starts running
  const loaded = useRef(false);
  const latest = useRef(state);
  useEffect(() => {
    latest.current = state;
  });
  const savedTotal = useRef(0);
  const runId = useRef<string | null>(null); // the run being timed; null until the first save creates one

  // Load the open run; time ticked while it loads is added on top.
  useEffect(() => {
    let live = true;
    loaded.current = false;
    api.timer(deckId, sessionCode).then(
      (run) => {
        if (!live) return;
        let times = run ? run.times : {};
        // This browser's timer from before it was stored (rehearsal only): move it into the database once.
        const legacyKey = `hali-shares:clock:${deckId}`;
        const legacy = sessionCode ? null : readStore<ClockState | null>(legacyKey, null);
        if (!run && legacy && total(legacy.times ?? {}) > 0) times = legacy.times;
        savedTotal.current = run ? total(run.times) : 0;
        runId.current = run ? run.id : null;
        loaded.current = true;
        setState((s) => ({ ...s, times: add(times, s.times), started: s.started || total(times) > 0 }));
        if (legacy) {
          api.saveTimer(deckId, null, add(times, latest.current.times), runId.current).then(
            (r) => {
              runId.current = r.id;
              removeStore(legacyKey);
            },
            () => {}, // kept locally; tried again on the next load
          );
        }
      },
      () => {
        loaded.current = true; // offline: keep counting, save when we can
      },
    );
    return () => {
      live = false;
    };
  }, [deckId, sessionCode, setState]);

  const save = useCallback(
    (keepalive = false) => {
      if (!loaded.current) return;
      const times = latest.current.times;
      const t = total(times);
      if (t === savedTotal.current) return;
      const forRun = runId.current;
      api.saveTimer(deckId, sessionCode, times, forRun, keepalive).then(
        (r) => {
          if (r.stale || runId.current !== forRun) return; // reset meanwhile: this belonged to the old run
          runId.current = r.id;
          savedTotal.current = t;
        },
        () => {}, // offline or signed out: the next save sends the whole map again
      );
    },
    [deckId, sessionCode],
  );

  useEffect(() => {
    if (!state.running) return;
    last.current = Date.now();
    const t = window.setInterval(() => {
      const now = Date.now();
      const elapsed = now - last.current;
      last.current = now;
      setState((s) => tick(s, section.current, elapsed));
    }, TICK_MS);
    const s = window.setInterval(() => save(), SAVE_MS);
    return () => {
      window.clearInterval(t);
      window.clearInterval(s);
      save(); // paused, or leaving
    };
  }, [state.running, save, setState]);

  useEffect(() => {
    const onHide = () => save(true);
    window.addEventListener("pagehide", onHide);
    return () => window.removeEventListener("pagehide", onHide);
  }, [save]);

  const start = useCallback(
    () => setState((s) => (s.started ? s : { ...s, started: true, running: true })),
    [setState],
  );
  const toggle = useCallback(() => setState((s) => ({ ...s, started: true, running: !s.running })), [setState]);
  const reset = useCallback(() => {
    // One call finishes the run with its last seconds. Clearing the refs first means the save that the stopping
    // interval makes on its way out has nothing to send, and any save already in flight is for the finished run.
    const times = latest.current.times;
    latest.current = EMPTY;
    savedTotal.current = 0;
    runId.current = null;
    setState(() => EMPTY);
    void api.resetTimer(deckId, sessionCode, total(times) > 0 ? times : undefined).catch(() => {});
  }, [deckId, sessionCode, setState]);
  return { ...state, start, toggle, reset };
}
